import type { LoroMap, LoroTreeNode, TreeID } from "loro-crdt";
import { LoroMap as LoroMapCtor, LoroText } from "loro-crdt";
import type { LucideIcon } from "lucide-react";
import { AlignLeft, Box, Code2, FileImage, Film, FlaskConical, FlaskRound, Globe, Layers, ListChecks, MonitorPlay, Music, FileSymlink, Link2, Paperclip, Share2, Table, Workflow } from "lucide-react";
import type { ComponentType } from "react";
import type { BlockType } from "@kss/schema";
import type { DocHandle } from "../sync/docManager";
import { SectionBlock } from "./section/SectionBlock";
import { CodeBlock } from "./code/CodeBlock";
import { MermaidBlock } from "./mermaid/MermaidBlock";
import { GroupBlock } from "./group/GroupBlock";
import { ImageBlock } from "./image/ImageBlock";
import { AudioBlock } from "./audio/AudioBlock";
import { VideoBlock } from "./video/VideoBlock";
import { ExternalVideoBlock } from "./external-video/ExternalVideoBlock";
import { ChemistryBlock } from "./chemistry/ChemistryBlock";
import { ReactionBlock } from "./reaction/ReactionBlock";
import { FileBlock } from "./file/FileBlock";
import { ChecklistBlock } from "./checklist/ChecklistBlock";
import { initChecklistBody } from "./checklist/model";
import { initReactionBody } from "./reaction/model";
import { GraphViewBlock } from "./graph-view/GraphViewBlock";
import { WebPageBlock } from "./webpage/WebPageBlock";
import { initWebPageBody } from "./webpage/model";
import { MirrorBlock } from "./mirror/MirrorBlock";
import { PageLinkBlock } from "./page-link/PageLinkBlock";
import { UrlBlock } from "./url/UrlBlock";
import { TableBlock } from "./table/TableBlock";
import { initTableBody } from "./table/model";
import { PlaceholderBlock } from "./PlaceholderBlock";

export interface BlockComponentProps {
  handle: DocHandle;
  treeId: TreeID;
  node: LoroTreeNode;
  readOnly: boolean;
  userId: string;
  /** the workspace of the page showing the block (mirrors check their source against it, §8.18) */
  workspaceId?: string;
  onExitUp?: () => void;
  onExitDown?: () => void;
  onFocus?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export interface BlockDefinition {
  type: BlockType;
  /** catalogue key of the name shown in menus (spec §8.23) */
  labelKey: string;
  icon: LucideIcon;
  milestone: "M1" | "M2" | "M5" | "M8" | "M9" | "M12" | "M14" | "M15" | "M24" | "M25" | "M29";
  /**
   * [M21] what a user-set height does to this block (spec §8.6 item 8): `scroll` gives the block box
   * the height and scrolls its content inside it; `fill` stretches the block's own surface into it.
   */
  resize: "scroll" | "fill";
  initBody(body: LoroMap, init?: unknown): void;
  Component: ComponentType<BlockComponentProps>;
}

const text = (body: LoroMap, key: string, value = "") => {
  const t = body.setContainer(key, new LoroText());
  if (value) t.insert(0, value);
};

/** Block registry (spec §8.6). M2 block types are registered with a placeholder component. */
export const blockRegistry: Record<BlockType, BlockDefinition> = {
  section: {
    type: "section",
    labelKey: "block.type.section",
    icon: AlignLeft,
    milestone: "M1",
    resize: "scroll",
    initBody: (body) => {
      // loro-prosemirror initialises {nodeName, attributes, children} itself on first bind
      body.setContainer("pm", new LoroMapCtor());
    },
    Component: SectionBlock,
  },
  group: { type: "group", labelKey: "block.type.group", icon: Box, milestone: "M2", resize: "scroll", initBody: (b) => text(b, "title"), Component: GroupBlock },
  code: { type: "code", labelKey: "block.type.code", icon: Code2, milestone: "M2", resize: "scroll", initBody: (b) => { text(b, "source"); b.set("language", "plaintext"); }, Component: CodeBlock },
  mermaid: { type: "mermaid", labelKey: "block.type.mermaid", icon: Workflow, milestone: "M2", resize: "fill", initBody: (b) => text(b, "source"), Component: MermaidBlock },
  image: { type: "image", labelKey: "block.type.image", icon: FileImage, milestone: "M2", resize: "fill", initBody: (b) => { b.set("asset", null); b.set("alt", ""); text(b, "caption"); }, Component: ImageBlock },
  audio: { type: "audio", labelKey: "block.type.audio", icon: Music, milestone: "M2", resize: "scroll", initBody: (b) => { b.set("asset", null); text(b, "caption"); }, Component: AudioBlock },
  video: { type: "video", labelKey: "block.type.video", icon: Film, milestone: "M2", resize: "fill", initBody: (b) => { b.set("asset", null); text(b, "caption"); }, Component: VideoBlock },
  external_video: { type: "external_video", labelKey: "block.type.externalVideo", icon: MonitorPlay, milestone: "M2", resize: "fill", initBody: (b) => { b.set("provider", "generic"); b.set("videoId", null); b.set("url", ""); b.set("startSeconds", null); text(b, "caption"); }, Component: ExternalVideoBlock },
  chemistry: { type: "chemistry", labelKey: "block.type.chemistry", icon: FlaskConical, milestone: "M2", resize: "fill", initBody: (b) => { b.set("kind", "molecule"); b.set("ket", ""); b.set("molfile", ""); text(b, "caption"); }, Component: ChemistryBlock },
  checklist: { type: "checklist", labelKey: "block.type.checklist", icon: ListChecks, milestone: "M12", resize: "scroll", initBody: initChecklistBody, Component: ChecklistBlock },
  // made by pasting (spec §8.18), never from the add-block menu
  mirror: { type: "mirror", labelKey: "block.type.mirror", icon: Layers, milestone: "M15", resize: "scroll", initBody: (b) => b.set("source", null), Component: MirrorBlock },
  // [M25] a web or app address as a link; the block says what a click does (spec §7.4.15)
  // [M29] a GFM-shaped table of inline-Markdown cells (spec §7.4.16, §8.28)
  table: { type: "table", labelKey: "block.type.table", icon: Table, milestone: "M29", resize: "scroll", initBody: initTableBody, Component: TableBlock },
  url: { type: "url", labelKey: "block.type.url", icon: Link2, milestone: "M25", resize: "scroll", initBody: (b) => { b.set("url", ""); text(b, "caption"); b.set("action", "browser"); b.set("openIn", "tab"); }, Component: UrlBlock },
  // [M24] a card for another page, opened by a click (spec §7.4.14)
  page_link: { type: "page_link", labelKey: "block.type.pageLink", icon: FileSymlink, milestone: "M24", resize: "scroll", initBody: (b, init) => { b.set("target", (init as { target?: { docId: string } } | undefined)?.target ?? null); text(b, "caption"); }, Component: PageLinkBlock },
  webpage: { type: "webpage", labelKey: "block.type.webpage", icon: Globe, milestone: "M14", resize: "fill", initBody: initWebPageBody, Component: WebPageBlock },
  file: { type: "file", labelKey: "block.type.file", icon: Paperclip, milestone: "M9", resize: "scroll", initBody: (b) => { b.set("asset", null); text(b, "caption"); b.set("disposition", "download"); b.set("openIn", "tab"); }, Component: FileBlock },
  reaction: { type: "reaction", labelKey: "block.type.reaction", icon: FlaskRound, milestone: "M8", resize: "fill", initBody: initReactionBody, Component: ReactionBlock },
  graph_view: {
    type: "graph_view",
    labelKey: "block.type.graphView",
    icon: Share2,
    milestone: "M5",
    resize: "fill",
    initBody: (b, init) => {
      const i = (init ?? {}) as { title?: string; query?: string };
      text(b, "title", i.title ?? "");
      text(b, "query", i.query ?? "");
      b.set("layout", "fcose");
      b.setContainer("positions", new LoroMapCtor());
      b.setContainer("styleOverrides", new LoroMapCtor());
    },
    Component: GraphViewBlock,
  },
};

export function blockDef(type: BlockType): BlockDefinition {
  const d = blockRegistry[type];
  if (!d) throw new Error(`unknown block type ${type}`);
  return d;
}

export const availableBlockTypes: BlockType[] = (Object.keys(blockRegistry) as BlockType[]).filter((t) => blockDef(t).Component !== PlaceholderBlock);
